import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Truck,
  Package,
  Eye,
  ArrowLeft,
  CalendarBlank,
  Anchor,
  Airplane,
  MapPin,
  FileText,
  Spinner,
  ArrowRight,
  CheckCircle
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SHIPMENT_STATUS_COLORS = {
  pending: 'bg-[#F4A261]/10 text-[#B45309]',
  in_transit: 'bg-[#0F52BA]/10 text-[#0F52BA]',
  delivered: 'bg-[#2D6A4F]/10 text-[#2D6A4F]'
};

const SHIPMENT_STATUS_LABELS = {
  pending: 'Pending Pickup',
  in_transit: 'In Transit',
  delivered: 'Delivered'
};

const FreightPortalPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shipmentForm, setShipmentForm] = useState({
    shipping_mode: '',
    vessel_or_flight: '',
    voyage_flight_number: '',
    port_of_loading: '',
    port_of_discharge: '',
    bill_of_lading: '',
    required_sail_date: '',
    sail_date: '',
    original_eta: '',
    current_eta: '',
    change_reason: '',
    status: 'pending'
  });
  // Snapshot of tracked fields when the shipment was loaded — used to require a
  // change_reason whenever ETA/Sail/Vessel/Voyage are modified after first set.
  const [originalTracked, setOriginalTracked] = useState({
    vessel_or_flight: '',
    voyage_flight_number: '',
    sail_date: '',
    current_eta: '',
  });
  // In-Transit confirmation dialog state
  const [inTransitDialogOpen, setInTransitDialogOpen] = useState(false);
  const [inTransitConfirmed, setInTransitConfirmed] = useState(false);
  const [inTransitNote, setInTransitNote] = useState('');

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/freight-portal/my-shipments`, getAuthHeaders());
      setShipments(res.data);
    } catch (error) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const viewShipmentDetail = async (poId) => {
    try {
      const res = await axios.get(`${API_URL}/api/freight-portal/shipment/${poId}`, getAuthHeaders());
      setSelectedShipment(res.data);
      // Pre-fill form with existing shipment data
      if (res.data.shipment_details) {
        const sd = res.data.shipment_details;
        const filled = {
          shipping_mode: sd.shipping_mode || '',
          vessel_or_flight: sd.vessel_or_flight || '',
          voyage_flight_number: sd.voyage_flight_number || '',
          port_of_loading: sd.port_of_loading || '',
          port_of_discharge: sd.port_of_discharge || '',
          bill_of_lading: sd.bill_of_lading || '',
          required_sail_date: sd.required_sail_date ? sd.required_sail_date.split('T')[0] : '',
          sail_date: sd.sail_date ? sd.sail_date.split('T')[0] : '',
          original_eta: sd.original_eta ? sd.original_eta.split('T')[0] : '',
          current_eta: sd.estimated_arrival ? sd.estimated_arrival.split('T')[0] : '',
          change_reason: '',
          status: sd.status || 'pending'
        };
        setShipmentForm(filled);
        setOriginalTracked({
          vessel_or_flight: filled.vessel_or_flight,
          voyage_flight_number: filled.voyage_flight_number,
          sail_date: filled.sail_date,
          current_eta: filled.current_eta,
        });
      } else {
        const empty = {
          shipping_mode: '',
          vessel_or_flight: '',
          voyage_flight_number: '',
          port_of_loading: '',
          port_of_discharge: '',
          bill_of_lading: '',
          required_sail_date: '',
          sail_date: '',
          original_eta: '',
          current_eta: '',
          change_reason: '',
          status: 'pending'
        };
        setShipmentForm(empty);
        setOriginalTracked({
          vessel_or_flight: '',
          voyage_flight_number: '',
          sail_date: '',
          current_eta: '',
        });
      }
    } catch (error) {
      toast.error('Failed to load shipment details');
    }
  };

  // Returns labels of tracked fields that have changed vs the original snapshot.
  const detectChangedTrackedFields = () => {
    const labels = {
      vessel_or_flight: 'Vessel Name',
      voyage_flight_number: 'Voyage Number',
      sail_date: 'Sail Date',
      current_eta: 'ETA',
    };
    const changed = [];
    Object.keys(labels).forEach(k => {
      const before = (originalTracked[k] || '').trim();
      const after = (shipmentForm[k] || '').trim();
      if (before && after && before !== after) {
        changed.push(labels[k]);
      }
    });
    return changed;
  };

  const saveShipmentDetails = async () => {
    if (!selectedShipment) return;

    const changedFields = detectChangedTrackedFields();
    if (changedFields.length > 0 && !(shipmentForm.change_reason || '').trim()) {
      toast.error(`Please provide a reason for changing: ${changedFields.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      // Build payload WITHOUT `status` — status transitions are explicit (Mark as In Transit button).
      // Avoids accidentally re-triggering the in-transit confirmation gate when editing fields
      // on a shipment that's already in_transit or has a cached status in the form.
      const { status: _ignored, ...rest } = shipmentForm;
      const payload = {
        ...rest,
        required_sail_date: shipmentForm.required_sail_date ? new Date(shipmentForm.required_sail_date).toISOString() : null,
        sail_date: shipmentForm.sail_date ? new Date(shipmentForm.sail_date).toISOString() : null,
        original_eta: shipmentForm.original_eta ? new Date(shipmentForm.original_eta).toISOString() : null,
        current_eta: shipmentForm.current_eta ? new Date(shipmentForm.current_eta).toISOString() : null,
        change_reason: (shipmentForm.change_reason || '').trim() || null,
      };
      await axios.put(
        `${API_URL}/api/freight-portal/shipment/${selectedShipment.id}`,
        payload,
        getAuthHeaders()
      );
      toast.success(
        changedFields.length > 0
          ? `Shipment updated. PM has been notified of changes.`
          : 'Shipment details saved successfully'
      );
      // Clear reason after save so it's required again on the next edit.
      setShipmentForm(prev => ({ ...prev, change_reason: '' }));
      await viewShipmentDetail(selectedShipment.id);
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shipment details');
    } finally {
      setSubmitting(false);
    }
  };

  const openInTransitDialog = () => {
    setInTransitConfirmed(false);
    setInTransitNote('');
    setInTransitDialogOpen(true);
  };

  const confirmInTransit = async () => {
    if (!inTransitConfirmed) {
      toast.error('Please tick the confirmation statement to continue.');
      return;
    }
    const trimmed = (inTransitNote || '').trim();
    if (trimmed.length < 10) {
      toast.error('Please list the documents you have uploaded for this shipment (min. 10 characters).');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...shipmentForm,
        status: 'in_transit',
        required_sail_date: shipmentForm.required_sail_date ? new Date(shipmentForm.required_sail_date).toISOString() : null,
        sail_date: shipmentForm.sail_date ? new Date(shipmentForm.sail_date).toISOString() : null,
        original_eta: shipmentForm.original_eta ? new Date(shipmentForm.original_eta).toISOString() : null,
        current_eta: shipmentForm.current_eta ? new Date(shipmentForm.current_eta).toISOString() : null,
        change_reason: (shipmentForm.change_reason || '').trim() || null,
        in_transit_confirmed: true,
        in_transit_confirmation_note: trimmed,
      };
      await axios.put(
        `${API_URL}/api/freight-portal/shipment/${selectedShipment.id}`,
        payload,
        getAuthHeaders()
      );
      toast.success('Shipment marked as In Transit');
      setShipmentForm(prev => ({ ...prev, status: 'in_transit' }));
      setInTransitDialogOpen(false);
      await viewShipmentDetail(selectedShipment.id);
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark In Transit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  // Detail View
  if (selectedShipment) {
    const shipmentStatus = selectedShipment.shipment_details?.status || 'pending';
    
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedShipment(null)} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedShipment.po_number}
            </h1>
            <p className="text-slate-500">Shipment Details</p>
          </div>
          <span className={`ml-auto px-3 py-1 rounded-full text-sm font-medium ${SHIPMENT_STATUS_COLORS[shipmentStatus]}`}>
            {SHIPMENT_STATUS_LABELS[shipmentStatus]}
          </span>
        </div>

        {/* PO Info Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Vendor</p>
            <p className="font-medium text-slate-800 mt-1">{selectedShipment.vendor_name}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Property</p>
            <p className="font-medium text-slate-800 mt-1">{selectedShipment.property_id}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Required Delivery</p>
            <p className="font-medium text-slate-800 mt-1">
              {selectedShipment.required_delivery_date ? format(new Date(selectedShipment.required_delivery_date), 'MMM d, yyyy') : '-'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Items</p>
            <p className="text-sm text-slate-600 mt-1">
              {selectedShipment.line_items?.length} items
            </p>
          </div>
        </div>

        {/* Item List — No financial info */}
        <div className="bg-white rounded-xl border border-slate-100">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Item List</h2>
            <p className="text-sm text-slate-500">Items to be shipped — quantities only</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ff-items-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Item Name</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Unit of Measure</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Quantity Ordered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {selectedShipment.line_items?.map((item, idx) => (
                  <tr key={idx} data-testid={`ff-item-row-${idx}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{item.item_name || item.description}</td>
                    <td className="px-4 py-3 text-center text-slate-600">{item.unit || item.unit_of_measure || '-'}</td>
                    <td className="px-4 py-3 text-center font-medium text-slate-700">{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Shipment Details Form */}
        <div className="bg-white rounded-xl border border-slate-100">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Shipment Details</h2>
            <p className="text-sm text-slate-500">Enter shipping information</p>
          </div>
          <div className="p-6">
            {/* Shipping Mode — must be selected first; labels below adapt to Ocean/Air. */}
            <div className="mb-6 p-4 rounded-lg border border-slate-200 bg-slate-50">
              <Label className="text-sm font-medium text-slate-800">
                Shipping Mode <span className="text-red-600">*</span>
              </Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-3">Select Ocean or Air. Form fields and the "Mark as In Transit" button update based on this choice.</p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Shipping Mode">
                {[
                  { id: 'ocean', label: 'Ocean Freight' },
                  { id: 'air', label: 'Air Freight' },
                ].map(opt => {
                  const active = shipmentForm.shipping_mode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setShipmentForm({ ...shipmentForm, shipping_mode: opt.id })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${active ? 'bg-[#0F52BA] text-white border-[#0F52BA]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#0F52BA] hover:text-[#0F52BA]'}`}
                      data-testid={`shipping-mode-${opt.id}-btn`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {(() => {
              const isAir = shipmentForm.shipping_mode === 'air';
              if (isAir) {
                // Caribbean Air freight — simplified field set: AWB + ETA + Actual Departure Date only.
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Air Waybill (AWB) Number</Label>
                      <Input
                        value={shipmentForm.bill_of_lading}
                        onChange={(e) => setShipmentForm({ ...shipmentForm, bill_of_lading: e.target.value })}
                        placeholder="e.g., 001-12345678"
                        data-testid="bol-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Estimated Time of Arrival (ETA)</Label>
                      <Input
                        type="date"
                        value={shipmentForm.original_eta}
                        onChange={(e) => setShipmentForm({ ...shipmentForm, original_eta: e.target.value })}
                        data-testid="original-eta-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Current ETA (if different)</Label>
                      <Input
                        type="date"
                        value={shipmentForm.current_eta}
                        onChange={(e) => setShipmentForm({ ...shipmentForm, current_eta: e.target.value })}
                        data-testid="current-eta-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Actual Departure Date</Label>
                      <Input
                        type="date"
                        value={shipmentForm.sail_date}
                        onChange={(e) => setShipmentForm({ ...shipmentForm, sail_date: e.target.value })}
                        placeholder="Date the flight actually departed"
                        data-testid="sail-date-input"
                      />
                    </div>
                  </div>
                );
              }
              // Ocean Freight — full set of fields
              return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Required Sail Date</Label>
                <Input
                  type="date"
                  value={shipmentForm.required_sail_date}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, required_sail_date: e.target.value })}
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="required-sail-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Estimated Time of Arrival (ETA)</Label>
                <Input
                  type="date"
                  value={shipmentForm.original_eta}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, original_eta: e.target.value })}
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="original-eta-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Vessel Name</Label>
                <Input
                  value={shipmentForm.vessel_or_flight}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, vessel_or_flight: e.target.value })}
                  placeholder="e.g., MSC Mariella"
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="vessel-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Voyage Number</Label>
                <Input
                  value={shipmentForm.voyage_flight_number}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, voyage_flight_number: e.target.value })}
                  placeholder="e.g., VOY-2026-001"
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="voyage-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Port of Loading</Label>
                <Input
                  value={shipmentForm.port_of_loading}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, port_of_loading: e.target.value })}
                  placeholder="e.g., Miami, FL"
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="pol-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Port of Discharge</Label>
                <Input
                  value={shipmentForm.port_of_discharge}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, port_of_discharge: e.target.value })}
                  placeholder="e.g., Kingston, Jamaica"
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="pod-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Bill of Lading</Label>
                <Input
                  value={shipmentForm.bill_of_lading}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, bill_of_lading: e.target.value })}
                  placeholder="e.g., MAEU123456789"
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="bol-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Current ETA (if different)</Label>
                <Input
                  type="date"
                  value={shipmentForm.current_eta}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, current_eta: e.target.value })}
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="current-eta-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Actual Sail Date</Label>
                <Input
                  type="date"
                  value={shipmentForm.sail_date}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, sail_date: e.target.value })}
                  placeholder="Date the vessel actually sailed"
                  disabled={!shipmentForm.shipping_mode}
                  data-testid="sail-date-input"
                />
              </div>
            </div>
              );
            })()}

            {/* Reason for change — always visible. Required when ETA / Sail / Vessel / Voyage are modified. */}
            {(() => {
              const changedFields = detectChangedTrackedFields();
              const required = changedFields.length > 0;
              return (
                <div
                  className={`mt-4 p-4 rounded-lg border ${required ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                  data-testid="change-reason-banner"
                >
                  <Label className={`text-sm font-medium ${required ? 'text-amber-900' : 'text-slate-700'}`}>
                    Reason for change {required && <span className="text-red-600">*</span>}
                  </Label>
                  <p className={`text-xs mt-0.5 mb-2 ${required ? 'text-amber-800' : 'text-slate-500'}`}>
                    {required ? (
                      <>You are updating: <strong>{changedFields.join(', ')}</strong>. Please provide a brief reason — recorded in the audit trail and the PM will be notified.</>
                    ) : (
                      <>If you change <strong>ETA</strong>, <strong>Sail Date</strong>, <strong>Vessel Name</strong>, or <strong>Voyage Number</strong> later, you'll be asked for a reason here. The PM is notified of every change.</>
                    )}
                  </p>
                  <Input
                    value={shipmentForm.change_reason}
                    onChange={(e) => setShipmentForm({ ...shipmentForm, change_reason: e.target.value })}
                    placeholder="e.g., Vessel delayed at port, cargo transferred to MSC Brittany"
                    data-testid="change-reason-input"
                  />
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-slate-100">
              <Button
                onClick={saveShipmentDetails}
                disabled={submitting || !shipmentForm.shipping_mode}
                className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
                data-testid="save-shipment-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Save Shipment Details
              </Button>
              
              {shipmentStatus === 'pending' && shipmentForm.shipping_mode && (
                <Button
                  onClick={openInTransitDialog}
                  disabled={submitting}
                  variant="outline"
                  className="border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F]/10"
                  data-testid="mark-transit-btn"
                >
                  <ArrowRight size={18} className="mr-2" />
                  Mark as In Transit
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Vendor Documents Section */}
        <div className="bg-white rounded-xl border border-slate-100">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Vendor Documents</h2>
            <p className="text-xs text-slate-500 mt-1">Reference documents uploaded by the vendor for this shipment.</p>
          </div>
          <div className="p-4">
            {selectedShipment.vendor_documents && selectedShipment.vendor_documents.length > 0 ? (
              <div className="space-y-3">
                {[...selectedShipment.vendor_documents]
                  .sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0))
                  .map((doc, idx) => {
                    const docTypeLabels = {
                      'original_vendor_invoice': 'Invoice',
                      'invoice': 'Invoice',
                      'vendor_invoice': 'Invoice',
                      'commercial_invoice': 'Invoice',
                      'proforma_invoice': 'Invoice',
                      'packing_list': 'Packing List',
                      'hazmat_documents': 'Hazmat Documents',
                      'material_data_safety_sheet': 'Material Data Safety Sheet',
                      'certificate_of_origin': 'Certificate of Origin',
                      'other': 'Other Document',
                    };
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                        data-testid={`ff-vendor-doc-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={24} className="text-slate-500" />
                          <div>
                            <p className="font-medium text-slate-800">{doc.filename}</p>
                            <p className="text-xs text-slate-500">
                              {docTypeLabels[doc.type] || doc.type}
                              {doc.uploaded_at && ` • ${format(new Date(doc.uploaded_at), 'MMM d, yyyy')}`}
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          Download
                        </Button>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <FileText size={32} className="mx-auto mb-2 text-slate-300" />
                <p>No vendor documents yet</p>
                <p className="text-xs text-slate-400 mt-1">Documents uploaded by the vendor will appear here automatically.</p>
              </div>
            )}
          </div>
        </div>

        {/* In-Transit Confirmation Dialog */}
        <Dialog open={inTransitDialogOpen} onOpenChange={setInTransitDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="in-transit-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Mark Shipment as In Transit</DialogTitle>
              <DialogDescription>
                You know this shipment best. Confirm you have uploaded every document relevant to it
                (sea freight: BoL + packing list + customs declaration; air freight: AWB + packing list;
                LCL: HBL + MODS sheets; plus any health/fumigation certificates as needed).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <Checkbox
                  id="in-transit-confirm-checkbox"
                  checked={inTransitConfirmed}
                  onCheckedChange={(v) => setInTransitConfirmed(!!v)}
                  data-testid="in-transit-confirm-checkbox"
                />
                <Label htmlFor="in-transit-confirm-checkbox" className="text-sm font-medium leading-snug cursor-pointer">
                  I confirm that all documents relevant to this shipment have been uploaded.
                </Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="in-transit-note" className="text-sm">
                  Documents uploaded <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="in-transit-note"
                  value={inTransitNote}
                  onChange={(e) => setInTransitNote(e.target.value)}
                  placeholder="e.g., Uploaded — House Bill of Lading, Packing List, Customs Declaration, Health Certificate"
                  rows={4}
                  data-testid="in-transit-confirm-note"
                />
                <p className="text-xs text-slate-500">
                  This note is permanently recorded on the PO audit trail along with your name and the date & time.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setInTransitDialogOpen(false)}
                disabled={submitting}
                data-testid="in-transit-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmInTransit}
                disabled={submitting || !inTransitConfirmed || (inTransitNote || '').trim().length < 10}
                className="bg-[#2D6A4F] hover:bg-[#1f4e37]"
                data-testid="in-transit-confirm-submit"
              >
                {submitting
                  ? <Spinner size={16} className="animate-spin mr-2" />
                  : <CheckCircle size={16} className="mr-2" />}
                Confirm & Mark In Transit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#0891B2]/10 flex items-center justify-center">
          <Truck size={24} className="text-[#0891B2]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Freight Forwarder Portal
          </h1>
          <p className="text-slate-500">Welcome, {user?.full_name}</p>
        </div>
      </div>

      {/* Key Actions - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#0891B2] to-[#22D3EE] rounded-xl p-6 text-white">
          <Anchor size={32} className="mb-3" />
          <h3 className="text-xl font-bold">Update Shipments</h3>
          <p className="text-white/80 text-sm mt-1">{shipments.filter(s => !s.shipment_details?.status || s.shipment_details?.status === 'pending').length} shipments need updates</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <MapPin size={32} className="text-[#0891B2] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">Track Cargo</h3>
          <p className="text-slate-500 text-sm mt-1">{shipments.filter(s => s.shipment_details?.status === 'in_transit').length} shipments in transit</p>
        </div>
      </div>

      {/* My Shipments */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">My Shipments</h2>
          <p className="text-sm text-slate-500">{shipments.length} shipments assigned</p>
        </div>

        {shipments.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Truck size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No shipments assigned to you</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Property</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shipments.map(shipment => {
                  const shipmentStatus = shipment.shipment_details?.status || 'pending';
                  return (
                    <tr key={shipment.id} className="hover:bg-slate-50/50" data-testid={`ff-shipment-row-${shipment.id}`}>
                      <td className="px-4 py-4 font-medium text-slate-800">{shipment.po_number}</td>
                      <td className="px-4 py-4 text-slate-600">{shipment.vendor_name}</td>
                      <td className="px-4 py-4 text-slate-600">{shipment.property_id}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${SHIPMENT_STATUS_COLORS[shipmentStatus]}`}>
                          {SHIPMENT_STATUS_LABELS[shipmentStatus]}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Button variant="ghost" size="sm" onClick={() => viewShipmentDetail(shipment.id)} data-testid={`view-shipment-${shipment.id}`}>
                          <Eye size={18} className="mr-1" />
                          View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreightPortalPage;
